// SMS AI API - API integration for AI-powered SMS generation
// Uses ApiClient with dependency injection pattern

import type { ApiClient } from '@commander/api-client';
import type {
  AiGeneration,
  AiGenerationCreateData,
  AiGenerationCreateResponse,
  BackendAiGeneration,
} from '../types/aiGeneration';

/**
 * Transform backend AI generation response to frontend type
 */
function transformAiGeneration(backend: BackendAiGeneration): AiGeneration {
  return {
    id: backend.id,
    type: backend.type,
    prompt: backend.prompt,
    status: backend.status,
    subject: backend.subject,
    body: backend.body,
    errorMessage: backend.error_message,
    createdAt: new Date(backend.created_at),
    updatedAt: new Date(backend.updated_at),
  };
}

/**
 * SMS AI API service factory
 * Provides methods for AI-powered SMS generation
 */
export function createSmsAiApi(apiClient: ApiClient) {
  return {
    /**
     * Generate SMS content with AI
     * @param data - Generation request data
     * @returns Generation ID and status
     */
    async generateSms(data: AiGenerationCreateData): Promise<AiGenerationCreateResponse> {
      const response = await apiClient.post<{ data: AiGenerationCreateResponse }>(
        '/communications/sms/ai',
        data
      );
      return response.data;
    },

    /**
     * Get AI generation result
     * @param generationId - ID of the generation to fetch
     * @returns AI generation result with plain text body (if completed)
     */
    async getGeneration(generationId: string): Promise<AiGeneration> {
      const response = await apiClient.get<{ data: BackendAiGeneration }>(
        `/communications/sms/ai/${generationId}`
      );
      return transformAiGeneration(response.data);
    },
  };
}
